import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Music, Upload, Play, Pause, Heart, Trash2, Star, CheckCircle, Send, X, Plus, ListMusic } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

export default function ArtistHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("tracks");
  const [tracks, setTracks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [songRequests, setSongRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === "tracks") {
        const res = await fetch(`${API}/tracks`, { credentials: "include" });
        if (res.ok) setTracks(await res.json());
      } else if (activeTab === "artists") {
        const res = await fetch(`${API}/artists`, { credentials: "include" });
        if (res.ok) setArtists(await res.json());
      } else if (activeTab === "requests") {
        const res = await fetch(`${API}/song-requests/received`, { credentials: "include" });
        if (res.ok) setSongRequests(await res.json());
      }
    } catch (e) { toast.error("Failed to load data"); }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const likeTrack = async (trackId) => {
    const res = await fetch(`${API}/tracks/${trackId}/like`, { method: "POST", credentials: "include" });
    if (res.ok) fetchData();
  };

  const deleteTrack = async (trackId) => {
    const res = await fetch(`${API}/tracks/${trackId}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { toast.success("Track deleted"); fetchData(); }
  };

  const updateRequest = async (requestId, status) => {
    const res = await fetch(`${API}/song-requests/${requestId}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (res.ok) { toast.success(`Request ${status}`); fetchData(); }
  };

  const requestVerification = async () => {
    const res = await fetch(`${API}/artist/request-verification`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ portfolio_url: "", reason: "I am a music artist" }),
    });
    if (res.ok) {
      toast.success("Artist verification approved!");
      window.location.reload();
    } else {
      const err = await res.json();
      toast.error(err.detail || "Failed");
    }
  };

  const tabs = [
    { id: "tracks", label: "All Tracks", icon: Music },
    { id: "artists", label: "Artists", icon: Star },
    ...(user?.is_artist ? [{ id: "requests", label: "Song Requests", icon: ListMusic }] : []),
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #000022 0%, #000044 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-6 max-w-4xl">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold glitter-text" data-testid="artist-hub-title">Music Hub</h1>
          <div className="flex gap-2">
            {!user?.artist_verified && (
              <button onClick={requestVerification} className="myspace-btn flex items-center gap-2 text-sm" data-testid="get-verified-btn">
                <Star className="w-4 h-4" /> Get Verified
              </button>
            )}
            {user?.is_artist && (
              <button onClick={() => setShowUpload(true)} className="myspace-btn-primary flex items-center gap-2" data-testid="upload-track-btn">
                <Upload className="w-4 h-4" /> Upload Track
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 font-bold text-sm transition-all ${activeTab === t.id ? "bg-gradient-to-r from-[#FF00FF] to-[#CC00CC] text-white border-2 border-[#FF00FF]" : "myspace-btn"}`}
              style={activeTab === t.id ? { boxShadow: "0 0 15px #FF00FF" } : {}}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-12 h-12 border-4 border-[#FF00FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Tracks Tab */}
            {activeTab === "tracks" && (
              <div className="space-y-3">
                {tracks.length === 0 ? (
                  <div className="myspace-card text-center py-12">
                    <Music className="w-12 h-12 mx-auto mb-3 text-[#FF00FF]" />
                    <p className="text-[#FF66FF]">No tracks yet. Artists can upload music!</p>
                  </div>
                ) : tracks.map(track => (
                  <TrackCard key={track.track_id} track={track} user={user} onLike={likeTrack} onDelete={deleteTrack} />
                ))}
              </div>
            )}

            {/* Artists Tab */}
            {activeTab === "artists" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {artists.length === 0 ? (
                  <div className="myspace-card text-center py-12 md:col-span-2">
                    <Star className="w-12 h-12 mx-auto mb-3 text-[#FF6600]" />
                    <p className="text-[#FF66FF]">No verified artists yet.</p>
                  </div>
                ) : artists.map(artist => (
                  <div key={artist.user_id} className="myspace-card" data-testid={`artist-card-${artist.user_id}`}>
                    <div className="flex items-center gap-3">
                      {artist.picture ? (
                        <img src={artist.picture} alt="" className="w-12 h-12 border-2 border-[#FF00FF] object-cover" />
                      ) : (
                        <div className="w-12 h-12 border-2 border-[#FF00FF] bg-[#330066] flex items-center justify-center text-lg text-[#FF66FF]">{artist.name?.[0]}</div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[#00FFFF] font-bold cursor-pointer hover:underline" onClick={() => navigate(`/profile/${artist.user_id}`)}>
                            {artist.name}
                          </span>
                          {artist.artist_verified && (
                            <span className="text-[#FFD700]" title="Verified Artist" style={{ filter: "drop-shadow(0 0 4px #FFD700)" }}>
                              <CheckCircle className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#6699CC]">{artist.artist_genre || "Artist"}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { setSelectedArtist(artist); setShowRequestForm(true); }} className="myspace-btn text-xs px-2 py-1" data-testid={`request-song-${artist.user_id}`}>
                          Request Song
                        </button>
                        <button onClick={() => navigate(`/tip/${artist.user_id}`)} className="myspace-btn-primary text-xs px-2 py-1" data-testid={`tip-artist-${artist.user_id}`}>
                          Tip
                        </button>
                      </div>
                    </div>
                    {artist.artist_bio && <p className="text-sm text-[#99CCFF] mt-2">{artist.artist_bio}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Song Requests Tab */}
            {activeTab === "requests" && (
              <div className="space-y-3">
                {songRequests.length === 0 ? (
                  <div className="myspace-card text-center py-12">
                    <ListMusic className="w-12 h-12 mx-auto mb-3 text-[#FF00FF]" />
                    <p className="text-[#FF66FF]">No song requests yet.</p>
                  </div>
                ) : songRequests.map(req => (
                  <div key={req.request_id} className="myspace-card" data-testid={`song-request-${req.request_id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[#00FFFF] font-bold">{req.song_title}</p>
                        <p className="text-xs text-[#FF66FF]">From: {req.requester_name}</p>
                        {req.message && <p className="text-sm text-[#99CCFF] mt-1">"{req.message}"</p>}
                      </div>
                      <div className="flex gap-2">
                        {req.status === "pending" ? (
                          <>
                            <button onClick={() => updateRequest(req.request_id, "accepted")} className="myspace-btn-primary text-xs px-3 py-1">Accept</button>
                            <button onClick={() => updateRequest(req.request_id, "declined")} className="myspace-btn text-xs px-3 py-1">Decline</button>
                          </>
                        ) : (
                          <span className={`text-xs font-bold ${req.status === "accepted" ? "text-[#00FF00]" : "text-[#FF6666]"}`}>
                            {req.status.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Upload Track Modal */}
      <AnimatePresence>
        {showUpload && <TrackUploadModal onClose={() => setShowUpload(false)} onSuccess={() => { setShowUpload(false); fetchData(); }} />}
        {showRequestForm && selectedArtist && (
          <SongRequestModal artist={selectedArtist} onClose={() => { setShowRequestForm(false); setSelectedArtist(null); }} />
        )}
      </AnimatePresence>
    </div>
  );
}

function TrackCard({ track, user, onLike, onDelete }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else {
      audioRef.current.play();
      fetch(`${API}/tracks/${track.track_id}/play`, { method: "POST", credentials: "include" });
    }
    setPlaying(!playing);
  };

  return (
    <div className="myspace-card" data-testid={`track-card-${track.track_id}`}>
      <audio ref={audioRef} src={track.audio_url} onEnded={() => setPlaying(false)} />
      <div className="flex items-center gap-3">
        <button onClick={togglePlay} className="w-10 h-10 flex-shrink-0 border-2 border-[#FF00FF] flex items-center justify-center bg-[rgba(255,0,255,0.1)] hover:bg-[rgba(255,0,255,0.2)] transition-all" data-testid={`play-track-${track.track_id}`}>
          {playing ? <Pause className="w-5 h-5 text-[#FF00FF]" /> : <Play className="w-5 h-5 text-[#FF00FF]" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[#00FFFF] font-bold truncate">{track.title}</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#FF66FF]">{track.artist_name}</span>
            {track.genre && <span className="interest-tag text-xs">{track.genre}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-[#6699CC]">{track.plays} plays</span>
          <button onClick={() => onLike(track.track_id)}
            className={`flex items-center gap-1 ${track.likes?.includes(user?.user_id) ? "text-[#FF0066]" : "text-[#FF66FF]"}`}
            data-testid={`like-track-${track.track_id}`}
          >
            <Heart className="w-4 h-4" fill={track.likes?.includes(user?.user_id) ? "currentColor" : "none"} />
            {track.likes?.length || 0}
          </button>
          {track.user_id === user?.user_id && (
            <button onClick={() => onDelete(track.track_id)} className="text-[#FF0000]" data-testid={`delete-track-${track.track_id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function TrackUploadModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [genre, setGenre] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file || !title.trim()) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await fetch(`${API}/upload/audio`, { method: "POST", credentials: "include", body: formData });
      if (!uploadRes.ok) throw new Error("Upload failed");
      const uploadData = await uploadRes.json();
      const audioUrl = `${API}/files/${uploadData.file_id}`;

      const trackRes = await fetch(`${API}/tracks`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: title.trim(), genre: genre.trim(), audio_url: audioUrl }),
      });
      if (!trackRes.ok) throw new Error("Failed to create track");
      toast.success("Track uploaded!");
      onSuccess();
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="myspace-card w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="track-upload-modal">
        <div className="flex justify-between mb-4">
          <h2 className="text-xl font-bold text-[#00FFFF]">Upload Track</h2>
          <button onClick={onClose} className="myspace-btn px-2 py-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <input ref={fileRef} type="file" accept="audio/*" onChange={e => setFile(e.target.files[0])} className="hidden" data-testid="audio-file-input" />
          {!file ? (
            <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-[#FF00FF] p-8 text-center cursor-pointer hover:border-[#00FFFF]">
              <Music className="w-10 h-10 mx-auto mb-2 text-[#FF00FF]" />
              <p className="text-[#FF66FF]">Select audio file</p>
              <p className="text-xs text-[#6699CC]">MP3, WAV, OGG, M4A, AAC, FLAC</p>
            </div>
          ) : (
            <div className="p-3 border border-[#6600CC] bg-[rgba(102,0,204,0.1)]">
              <p className="text-[#00FFFF] text-sm truncate">{file.name}</p>
              <button onClick={() => setFile(null)} className="text-xs text-[#FF6666] mt-1">Remove</button>
            </div>
          )}
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Track title" className="myspace-input" data-testid="track-title-input" />
          <input type="text" value={genre} onChange={e => setGenre(e.target.value)} placeholder="Genre (optional)" className="myspace-input" data-testid="track-genre-input" />
          <button onClick={handleUpload} disabled={!file || !title.trim() || uploading} className="myspace-btn-primary w-full disabled:opacity-50" data-testid="submit-track-btn">
            {uploading ? "Uploading..." : "Upload Track"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function SongRequestModal({ artist, onClose }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/song-requests`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ artist_user_id: artist.user_id, song_title: title.trim(), message: message.trim() }),
      });
      if (res.ok) { toast.success("Song requested!"); onClose(); }
      else { const err = await res.json(); toast.error(err.detail); }
    } catch (e) { toast.error("Failed to send request"); }
    setSending(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="myspace-card w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="song-request-modal">
        <h2 className="text-xl font-bold text-[#00FFFF] mb-4">Request Song from {artist.name}</h2>
        <div className="space-y-4">
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Song title" className="myspace-input" data-testid="request-song-title" />
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Message (optional)" className="myspace-input min-h-[60px] resize-none" data-testid="request-song-message" />
          <div className="flex gap-3">
            <button onClick={onClose} className="myspace-btn flex-1">Cancel</button>
            <button onClick={handleSubmit} disabled={!title.trim() || sending} className="myspace-btn-primary flex-1 disabled:opacity-50" data-testid="submit-song-request-btn">
              {sending ? "Sending..." : "Send Request"}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
